import { IconButton } from '@mui/joy';
import {
  DotsThreeOutlineVertical,
  DownloadSimple,
  Info,
  Trash,
} from '@phosphor-icons/react';
import { MouseEvent, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { DirectoryPathProps } from '../../TabDisplay';
import MoveFolderModal from '../modal/MoveFolderModal';
import RenameFolderModal from '../modal/RenameFolderModal';

import { Button } from '~/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import {
  AttachmentFile,
  AttachmentFolder,
  ReportAttachementProps,
} from '~/services/api/reports/types';
import { setDisplayFile, setFileDetails } from '~/slices/reportDataSlice';

type FileOrFolder = AttachmentFile | AttachmentFolder;

interface OtherOptionPopoverProps {
  absolute?: boolean;
  iconSize?: number;
  reportId: string | undefined;
  attachments: AttachmentFolder;
  attachmentsDisplay: FileOrFolder[];
  directoryPath?: DirectoryPathProps[] | undefined;
  itemInformation: ReportAttachementProps;
  deleteFolderAction: (
    items: {
      parentFolder?: number;
      fileId: number;
      isFolder: boolean;
    }[],
  ) => void;
}

const OtherOptionPopoverFolder = ({
  reportId,
  itemInformation,
  attachments,
  attachmentsDisplay,
  directoryPath,
  deleteFolderAction,
}: OtherOptionPopoverProps) => {
  const dispatch = useDispatch();
  const { openConfirmationModal } = useToastNotificationContext();

  const [open, setOpen] = useState(false);

  const handleDeleteCheckedItems = () => {
    openConfirmationModal(
      'Remove Folder Confirmation',
      'Please confirm if you want to remove this folder.',
      'Cancel',
      'Confirm',
      () => {
        deleteFolderAction([
          {
            fileId: itemInformation.id,
            isFolder: itemInformation.is_folder,
          },
        ]);
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <IconButton
          size='sm'
          onClick={(e) => {
            e.stopPropagation();
            setOpen((prev) => !prev);
          }}
        >
          <DotsThreeOutlineVertical weight='fill' />
        </IconButton>
      </PopoverTrigger>
      <PopoverContent
        side='bottom'
        align='start'
        sideOffset={10}
        bleed
        className='!max-w-32'
      >
        <div className='w-full'>
          {itemInformation.is_folder && (
            <RenameFolderModal
              reportId={reportId}
              folderId={itemInformation.id}
              parentId={directoryPath?.slice(-1)[0].id}
              currentDirectoryItems={attachmentsDisplay}
            />
          )}

          {!itemInformation.is_folder && (
            <Button
              variant='soft'
              color='neutral'
              className='block !justify-start w-full !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation();
                setOpen((prev) => !prev);
                // handleOptionClick(i.id, 'Option 3');
              }}
              startDecorator={<DownloadSimple size={22} />}
            >
              <FormattedMessage defaultMessage={'Download'} />
            </Button>
          )}

          <MoveFolderModal
            displayAsPopover
            checkedItems={[itemInformation]}
            reportId={reportId}
            attachments={attachments}
            attachmentsDisplay={attachmentsDisplay}
            directoryPath={directoryPath}
          />

          <Button
            variant='soft'
            color='neutral'
            className='block !justify-start w-full items-start !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
              dispatch(setDisplayFile(true));
              dispatch(setFileDetails(itemInformation));
            }}
            startDecorator={<Info size={20} color='#434c4c' />}
          >
            File information
          </Button>

          <hr className='border-gray-300 dark:border-gray-900' />
          <Button
            variant='soft'
            color='neutral'
            className='block !justify-start w-full items-start !rounded-none px-4 py-2 text-gray-800 hover:bg-gray-200'
            onClick={(event: MouseEvent<HTMLButtonElement>) => {
              event.stopPropagation();
              setOpen((prev) => !prev);
              handleDeleteCheckedItems();
            }}
            startDecorator={<Trash size={20} color='#434c4c' />}
          >
            <FormattedMessage defaultMessage={'Remove'} />
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default OtherOptionPopoverFolder;
